import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { cn } from "@/lib/utils";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { useQuery } from "@tanstack/react-query";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { apiRequest } from "@/lib/queryClient";

type TimeRange = "7days" | "30days" | "90days" | "1year";

interface UsageTrendsChartProps {
  timeRangeFilter?: string;
}

export default function UsageTrendsChart({ timeRangeFilter }: UsageTrendsChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("30days");

  // Update time range if the filter changes
  useEffect(() => {
    if (timeRangeFilter && (timeRangeFilter === "7days" || timeRangeFilter === "30days" || 
        timeRangeFilter === "90days" || timeRangeFilter === "1year")) {
      setTimeRange(timeRangeFilter as TimeRange);
    }
  }, [timeRangeFilter]);
  
  // Fetch usage trends data from the API with proper tenant context
  const { data: usageData, isLoading, error } = useQuery({
    queryKey: ['/api/analytics/usage-trends', timeRange],
    queryFn: async () => {
      return await apiRequest("GET", `/api/analytics/usage-trends?timeRange=${timeRange}`);
    }
  });

  const customTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const assigned = payload.find((p: any) => p.dataKey === 'active')?.value || 0;
      const available = payload.find((p: any) => p.dataKey === 'inactive')?.value || 0;
      const total = assigned + available;
      const utilization = total > 0 ? Math.round((assigned / total) * 100) : 0;
      
      return (
        <div className="bg-white p-4 border border-slate-300 shadow-lg rounded-lg">
          <p className="font-semibold text-slate-900 mb-2">{label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                <span className="text-sm text-slate-700">Assigned</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">{assigned} seats</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-slate-300"></div>
                <span className="text-sm text-slate-700">Available</span>
              </div>
              <span className="text-sm font-semibold text-slate-900">{available} seats</span>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">Total Seats:</span>
              <span className="text-sm font-bold text-slate-900">{total}</span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span className="text-sm font-medium text-slate-700">Utilization:</span>
              <span className={cn(
                "text-sm font-bold",
                utilization >= 80 ? "text-emerald-600" : utilization >= 50 ? "text-amber-600" : "text-red-600"
              )}>{utilization}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Display a loading state when data is being fetched
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-8 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
          <div className="h-80 w-full mt-4">
            <Skeleton className="h-full w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Display an error message if the fetch failed
  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load usage trends data. This could be due to a connection issue or server problem.
              Please try refreshing the page.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div className="flex items-center space-x-2">
          <CardTitle className="text-lg font-semibold">License Seat Allocation Trends</CardTitle>
          <Popover>
            <PopoverTrigger>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <InfoCircledIcon className="h-4 w-4 text-slate-500" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-slate-900">Understanding This Chart</h4>
                <p className="text-sm text-slate-600 leading-relaxed">
                  This stacked area chart visualizes your license seat utilization over time. The total height represents your total purchased seats.
                </p>
                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 mt-1 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Assigned Seats</p>
                      <p className="text-xs text-slate-600">Licenses actively allocated to team members</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <div className="w-3 h-3 rounded-full bg-slate-400 mt-1 flex-shrink-0"></div>
                    <div>
                      <p className="text-sm font-medium text-slate-900">Available Seats</p>
                      <p className="text-xs text-slate-600">Purchased licenses ready to assign</p>
                    </div>
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-xs text-slate-500">
                    💡 Hover over the chart to see utilization percentage and detailed breakdown for each date
                  </p>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="flex space-x-2">
          <Button 
            size="sm" 
            variant={timeRange === "7days" ? "default" : "outline"}
            onClick={() => setTimeRange("7days")}
            className="text-xs"
          >
            7 Days
          </Button>
          <Button 
            size="sm" 
            variant={timeRange === "30days" ? "default" : "outline"}
            onClick={() => setTimeRange("30days")}
            className="text-xs"
          >
            30 Days
          </Button>
          <Button 
            size="sm" 
            variant={timeRange === "90days" ? "default" : "outline"}
            onClick={() => setTimeRange("90days")}
            className="text-xs"
          >
            90 Days
          </Button>
          <Button 
            size="sm" 
            variant={timeRange === "1year" ? "default" : "outline"}
            onClick={() => setTimeRange("1year")}
            className="text-xs"
          >
            1 Year
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {usageData && usageData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={usageData}
                margin={{ top: 10, right: 30, left: 0, bottom: 20 }}
              >
                <defs>
                  <linearGradient id="colorAssigned" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
                  </linearGradient>
                  <linearGradient id="colorAvailable" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#cbd5e1" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#cbd5e1" stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid 
                  strokeDasharray="3 3" 
                  vertical={false} 
                  stroke="#e2e8f0" 
                />
                <XAxis 
                  dataKey="date" 
                  axisLine={{ stroke: '#cbd5e1' }}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickFormatter={(value) => {
                    const date = new Date(value);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  interval="preserveStartEnd"
                />
                <YAxis 
                  axisLine={{ stroke: '#cbd5e1' }}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#64748b' }}
                  tickFormatter={(value) => `${value}`}
                  width={60}
                  label={{ value: 'Number of Seats', angle: -90, position: 'insideLeft', style: { fontSize: 12, fill: '#64748b' } }}
                />
                <Tooltip content={customTooltip} />
                <Legend 
                  wrapperStyle={{ 
                    paddingTop: '20px',
                    fontSize: '14px'
                  }}
                  iconType="circle"
                />
                <Area
                  type="monotone"
                  dataKey="inactive"
                  name="Available Seats"
                  stackId="1"
                  stroke="#94a3b8"
                  strokeWidth={2}
                  fill="url(#colorAvailable)"
                />
                <Area
                  type="monotone"
                  dataKey="active"
                  name="Assigned Seats"
                  stackId="1"
                  stroke="#10b981"
                  strokeWidth={2}
                  fill="url(#colorAssigned)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="text-muted-foreground">
              <p className="text-lg font-medium mb-2">No license data available</p>
              <p className="text-sm">Add some licenses to see usage trends here.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
